import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import "./styles.scss";
import { ArrangedUseLimitsData } from "../../../models/featureUseLimits/FeatureUseLimitsData";
import DataSvc from "../../../services/dataSvc";
import EditUseLimitItem from "../EditUseLimitItem";

export interface IModalEditUseLimit {
  arrangeUseLimits: ArrangedUseLimitsData;
  onClose: () => void;
  onDelete?: () => void;
  onRefresh: () => void;
}

export const ModalEditUseLimit: React.FunctionComponent<IModalEditUseLimit> = ({
  arrangeUseLimits,
  onClose,
  onRefresh,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`functionManagementPage.modalEditUseLimit.${key}`);

  const [updateUseLimits, setUpdateUseLimits] = useState(new Map());
  const [currentMode, setCurrentMode] = useState("edit");

  const updateUseLimitMutation = useMutation((payload: any) => {
    return DataSvc.updateUseLimit(
      {
        target_id: Number(arrangeUseLimits.target_id),
        feature_id: Number(payload.feature.id),
        can_use: payload.can_use,
        can_use_times: payload.can_use_times,
        use_limit_cycle: payload.use_limit_cycle,
      },
      arrangeUseLimits.target
    );
  });

  const onClickSubmit = async () => {
    try {
      const updatePromises = Array.from(updateUseLimits.values()).map((item) =>
        updateUseLimitMutation.mutateAsync(item)
      );

      await Promise.allSettled(updatePromises);
      onRefresh();
      setCurrentMode("edit");
      onClose();
    } catch (error) {
      console.error("Error updating use limits:", error);
    }
  };

  const onToggleMode = () => {
    if (currentMode === "edit") {
      setCurrentMode("reset");
    } else {
      setCurrentMode("edit");
    }
  };

  return (
    <>
      <div className="modal modal-default modal-edit-feaetures">
        <div className="modal-mains">
          <button
            className="btn-close"
            onClick={() => {
              onClose();
            }}
          />

          <div className="top-title">
            {arrangeUseLimits?.email || arrangeUseLimits?.name || "-"}
          </div>
          {currentMode === "reset" && (
            <div className="reset-setting-hint">{t("back_to_default")}</div>
          )}
          {arrangeUseLimits.use_limits.map((useLimit) => (
            <EditUseLimitItem
              key={useLimit.feature.id}
              arrangedUseLimit={useLimit}
              mode={currentMode}
              onChange={(needUpdate, item) => {
                if (needUpdate) {
                  updateUseLimits.set(item.feature.id, item);
                } else {
                  updateUseLimits.delete(item.feature.id);
                }

                setUpdateUseLimits(new Map(updateUseLimits));
              }}
            />
          ))}
          <div className="bottom-btns">
            {currentMode === "edit" && (
              <>
                <button className="btn btn-border" onClick={onClose}>
                  {t("cancel")}
                </button>
                <button className="btn btn-red" onClick={onToggleMode}>
                  {t("reset")}
                </button>
                <button className={`btn btn-blue`} onClick={onClickSubmit}>
                  {t("submit")}
                </button>
              </>
            )}
            {currentMode === "reset" && (
              <>
                <button className="btn btn-border" onClick={onToggleMode}>
                  {t("back")}
                </button>
                <button className="btn btn-red" onClick={onClickSubmit}>
                  {t("confirm_reset")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalEditUseLimit;
